import {
  CSSProperties,
  forwardRef,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import { cn } from '../../utils';

interface TableProps extends HTMLAttributes<HTMLTableElement> {
  containerRef?: React.RefObject<HTMLDivElement>;
}

const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, containerRef, ...props }, ref) => (
    <div ref={containerRef} className="relative w-full overflow-auto">
      <table
        ref={ref}
        className={cn('w-full caption-bottom', className)}
        {...props}
      />
    </div>
  ),
);
Table.displayName = 'Table';

const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn('[&_tr]:border-b [&_tr]:last-of-type:border-0', className)}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('border-t [&>tr]:last:border-b-0', className)}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = forwardRef<
  HTMLTableRowElement,
  HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn('border-b transition-colors', className)}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

const TableHead = forwardRef<
  HTMLTableCellElement,
  ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'text-left align-middle [&:has([role=checkbox])]:pr-0',
      className,
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = forwardRef<
  HTMLTableCellElement,
  TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn('align-middle [&:has([role=checkbox])]:pr-0', className)}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = forwardRef<
  HTMLTableCaptionElement,
  HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn('mt-4', className)} {...props} />
));
TableCaption.displayName = 'TableCaption';

interface OverflowGradientLeftProps {
  isVisible: boolean;
  style?: CSSProperties;
}

const OverflowGradientLeft = ({
  isVisible,
  style,
}: OverflowGradientLeftProps) => (
  <div
    className={cn(
      'to-background-default duration-250 w-120 pointer-events-none absolute bottom-0 right-0 top-0 z-10 bg-gradient-to-r from-[hsl(var(--background-default)_/_0.1)] from-70% opacity-0 transition-opacity sm:from-20%',
      {
        'visible opacity-100': isVisible,
      },
    )}
    style={style}
  />
);

const OverflowGradientRight = ({
  isVisible,
  style,
}: OverflowGradientLeftProps) => (
  <div
    className={cn(
      'to-background-default duration-250 w-120 pointer-events-none absolute bottom-0 left-0 top-0 z-10 bg-gradient-to-l from-[hsl(var(--background-default)_/_0.1)] from-70% opacity-0 transition-opacity sm:from-20%',
      {
        'visible opacity-100': isVisible,
      },
    )}
    style={style}
  />
);

export {
  OverflowGradientLeft,
  OverflowGradientRight,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
